
/**
 * Password strength policy — frontend mirror of the backend single source of
 * truth (`backend/shared/services/password_policy.py`). Keep the two in sync.
 *
 * Rule (Gmail-style): 8+ characters with at least one letter, one number, and
 * one symbol.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  /** i18n key for the requirement label. */
  key: string;
  /** English fallback label — used only when the key is missing. */
  label: string;
  /** Interpolation values the key needs. Spread these at the call site. */
  params?: Record<string, unknown>;
  /** Whether the current password satisfies this rule. */
  met: boolean;
}

/**
 * Evaluate each requirement against a password — drives the live checklist UI.
 */
export function checkPasswordRules(password: string): PasswordRule[] {
  return [
    {
      key: 'passwordPolicy.length',
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      // The key interpolates {{count}}. Without this the placeholder renders.
      params: { count: PASSWORD_MIN_LENGTH },
      met: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    },
    { key: 'passwordPolicy.letter', label: 'At least one letter', met: /[A-Za-z]/.test(password) },
    { key: 'passwordPolicy.number', label: 'At least one number', met: /\d/.test(password) },
    { key: 'passwordPolicy.symbol', label: 'At least one symbol', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

/** A localizable validation error: an i18n key + English fallback (+ params).
 *  Resolve at the call site with `t(key, { ...params, defaultValue })`. */
export interface PasswordPolicyError {
  key: string;
  defaultValue: string;
  params?: Record<string, unknown>;
}

/**
 * Return the first failing rule as a localizable error, or null when valid.
 * Mirrors the backend's first-failing-rule error so client + server agree.
 * Returns an i18n key (not a literal) so callers can translate it — the
 * message can surface on the public, RTL/Hebrew reset page.
 */
export function validatePasswordPolicy(password: string): PasswordPolicyError | null {
  if (!password) {
    return { key: 'passwordPolicy.error.required', defaultValue: 'Password is required' };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      key: 'passwordPolicy.error.minLength',
      defaultValue: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
      params: { min: PASSWORD_MIN_LENGTH },
    };
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      key: 'passwordPolicy.error.maxLength',
      defaultValue: `Password must be at most ${PASSWORD_MAX_LENGTH} characters`,
      params: { max: PASSWORD_MAX_LENGTH },
    };
  }
  if (!/[A-Za-z]/.test(password)) {
    return { key: 'passwordPolicy.error.letter', defaultValue: 'Password must include at least one letter' };
  }
  if (!/\d/.test(password)) {
    return { key: 'passwordPolicy.error.number', defaultValue: 'Password must include at least one number' };
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return { key: 'passwordPolicy.error.symbol', defaultValue: 'Password must include at least one symbol' };
  }
  return null;
}

export function isPasswordValid(password: string): boolean {
  return validatePasswordPolicy(password) === null;
}
